import { IconButton, useNotify } from '@hotjar/hotsauce';
import { useState } from 'react';
import { deleteMfaTrustedDevice, useMfaTrustedDevices } from '@hotjar/webapp/domains/mfa';
import { useCurrentUser } from '@hotjar/user';
import { UAParser } from 'ua-parser-js';
import { moment } from '@hotjar/insights-common/lib/moment';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { DeviceTable } from './styles';
import { ConfirmationModal } from './ConfirmationModal';

const getDeviceName = (userAgent?: string): string => {
  let name = 'N/A';
  const parser = new UAParser(userAgent);
  const { name: browserName } = parser.getBrowser();
  if (browserName) {
    name = browserName;
  }

  const { name: osName } = parser.getOS();
  if (osName) {
    name = `${name} (${osName})`;
  }

  return name;
};

export const MfaTrustedDevices = (): JSX.Element | null => {
  const { t } = useTranslationWithPrefix('mfa:trusted-devices');
  const { data: currentUser } = useCurrentUser();
  const { data: trustedDevices, refetch: refetchMfaTrustedDevices } = useMfaTrustedDevices(
    currentUser?.id,
  );
  const { showToast } = useNotify();

  const [loading, setLoading] = useState(false);
  const [selectedDeviceId, setDeviceId] = useState<number>();

  const isModalOpen = selectedDeviceId !== undefined;

  const onDelete = () => {
    setDeviceId(undefined);
    if (currentUser?.id && selectedDeviceId) {
      setLoading(true);
      deleteMfaTrustedDevice(currentUser.id, selectedDeviceId)
        .then((response) => {
          if (response.success) {
            refetchMfaTrustedDevices();
            showToast({ kind: 'success', text: t('success-message') });
          } else {
            showToast({ kind: 'danger', text: t('error-message') });
          }
        })
        .catch(() => {
          showToast({ kind: 'danger', text: t('error-message') });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  if (trustedDevices === undefined || trustedDevices.length === 0) {
    return null;
  }

  return (
    <>
      <ConfirmationModal
        isOpen={isModalOpen}
        onDelete={onDelete}
        onCancel={() => setDeviceId(undefined)}
      />
      <DeviceTable>
        <div className="device-table-title">{t('table.title')}</div>
        <div className="device-table-body">
          {trustedDevices?.map((device) => (
            <div key={`TrustedDevice_${device.id}`} className="device-table-item">
              <div className="device">{getDeviceName(device.userAgent)}</div>
              <div className="time-stamp">{moment.unix(device.created).fromNow()}</div>
              <div className="action">
                <IconButton
                  isLoading={loading && selectedDeviceId === device.id}
                  tooltipText={t('table.remove')}
                  title={t('table.remove')}
                  kind="ghost"
                  size="s"
                  icon="close"
                  onClick={() => {
                    setDeviceId(device.id);
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </DeviceTable>
    </>
  );
};
